import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useAuth } from '../auth';
import { CompactTable } from '../components/CompactTable';
import { GuestInvitationPanel } from '../components/GuestInvitationPanel';
import {
  deleteAdminPortalUser,
  getAdminPortalUsers,
  getPortalEvents,
  importAdminEntraUser,
  PortalApiError,
  removePortalEventParticipant,
  releasePortalPhoneBooking,
  searchAdminEntraUsers,
  setPortalEventParticipant,
  updateAdminPortalUserActiveState,
  updateAdminPortalUserEventManager,
  updateAdminPortalUserMobileNumber,
} from '../services/portalApi';
import type {
  AdminPortalUser,
  EntraDirectorySearchUser,
  PortalEvent,
  PortalEventRole,
} from '../types/api';

type UserSortKey = 'name' | 'email' | 'organization' | 'roles' | 'status';
type UserSortDirection = 'ascending' | 'descending';

interface UserSort {
  key: UserSortKey;
  direction: UserSortDirection;
}

export function AdminUsersPage() {
  const { getAccessToken } = useAuth();
  const [users, setUsers] = useState<AdminPortalUser[]>([]);
  const [sort, setSort] = useState<UserSort | null>(null);
  const [events, setEvents] = useState<PortalEvent[]>([]);
  const [selectedLoginId, setSelectedLoginId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedRole, setSelectedRole] = useState<PortalEventRole>('Caller');
  const [mobileNumber, setMobileNumber] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<EntraDirectorySearchUser[]>([]);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const selectedUser = useMemo(
    () => users.find((user) => user.LoginId === selectedLoginId) ?? null,
    [selectedLoginId, users],
  );

  const sortedUsers = useMemo(() => {
    if (!sort) return users;

    const direction = sort.direction === 'ascending' ? 1 : -1;
    return [...users].sort((first, second) => (
      getUserSortValue(first, sort.key).localeCompare(
        getUserSortValue(second, sort.key),
        undefined,
        { numeric: true, sensitivity: 'base' },
      ) * direction
    ));
  }, [sort, users]);

  const refreshUsers = useCallback(async (): Promise<AdminPortalUser[]> => {
    const token = await getAccessToken();
    const response = await getAdminPortalUsers(token);
    setUsers(response.users);
    return response.users;
  }, [getAccessToken]);

  useEffect(() => {
    let cancelled = false;

    async function loadInitialData(): Promise<void> {
      try {
        const token = await getAccessToken();
        const userResponse = await getAdminPortalUsers(token);
        if (!cancelled) setUsers(userResponse.users);

        try {
          const eventResponse = await getPortalEvents(token);
          if (!cancelled) setEvents(eventResponse.events);
        } catch {
          if (!cancelled) {
            setMessage('Portal users were loaded, but events could not be loaded.');
          }
        }
      } catch (error) {
        if (!cancelled) setMessage(toMessage(error));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void loadInitialData();
    return () => { cancelled = true; };
  }, [getAccessToken]);

  useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < 2) return;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setIsSearching(true);
      setSearchMessage(null);
      try {
        const token = await getAccessToken();
        const response = await searchAdminEntraUsers(token, query);
        if (!cancelled) setSearchResults(response.users);
      } catch (error) {
        if (!cancelled) {
          setSearchResults([]);
          setSearchMessage(toMessage(error));
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [getAccessToken, searchQuery]);

  function selectUser(user: AdminPortalUser): void {
    setSelectedLoginId(user.LoginId);
    const firstRole = user.EventRoles[0];
    setSelectedEventId(firstRole?.EventId ?? events[0]?.EventId ?? '');
    setSelectedRole(firstRole?.Role ?? 'Caller');
    setMobileNumber(user.MobileNumber ?? '');
    setMessage(null);
  }

  function changeSelectedEvent(eventId: string): void {
    setSelectedEventId(eventId);
    const existingRole = selectedUser?.EventRoles.find(
      (eventRole) => eventRole.EventId === eventId,
    );
    setSelectedRole(existingRole?.Role ?? 'Caller');
  }

  async function importUser(user: EntraDirectorySearchUser): Promise<void> {
    setIsSaving(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      const response = await importAdminEntraUser(token, user.EntraObjectId);
      const refreshedUsers = await refreshUsers();
      setSelectedLoginId(response.user.LoginId);
      setSearchResults((current) => current.map((item) => (
        item.EntraObjectId === user.EntraObjectId
          ? { ...item, IsRegistered: true }
          : item
      )));
      const imported = refreshedUsers.find(
        (item) => item.LoginId === response.user.LoginId,
      );
      setSelectedEventId(imported?.EventRoles[0]?.EventId ?? events[0]?.EventId ?? '');
      setSelectedRole(imported?.EventRoles[0]?.Role ?? 'Caller');
      setMobileNumber(imported?.MobileNumber ?? '');
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsSaving(false);
    }
  }

  async function saveRole(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!selectedUser || !selectedEventId) return;

    setIsSaving(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await setPortalEventParticipant(token, selectedEventId, {
        loginId: selectedUser.LoginId,
        role: selectedRole,
      });
      await refreshUsers();
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsSaving(false);
    }
  }

  async function removeRole(eventId: string, eventName: string): Promise<void> {
    if (!selectedUser || !window.confirm(
      `Remove ${selectedUser.Email} from ${eventName}?`,
    )) return;

    setIsSaving(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await removePortalEventParticipant(token, eventId, selectedUser.LoginId);
      await refreshUsers();
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsSaving(false);
    }
  }

  function changeSort(key: UserSortKey): void {
    setSort((current) => {
      if (!current || current.key !== key) {
        return { key, direction: 'ascending' };
      }
      if (current.direction === 'ascending') {
        return { key, direction: 'descending' };
      }
      return null;
    });
  }

  function sortDirection(key: UserSortKey): UserSortDirection | undefined {
    return sort?.key === key ? sort.direction : undefined;
  }

  async function setUserActiveState(isActive: boolean): Promise<void> {
    if (!selectedUser) return;

    setIsSaving(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await updateAdminPortalUserActiveState(token, selectedUser.LoginId, isActive);
      await refreshUsers();
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsSaving(false);
    }
  }

  async function setUserEventManager(isEventManager: boolean): Promise<void> {
    if (!selectedUser) return;

    setIsSaving(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await updateAdminPortalUserEventManager(
        token,
        selectedUser.LoginId,
        isEventManager,
      );
      await refreshUsers();
      setMessage(
        isEventManager
          ? 'The user is now an Event Manager.'
          : 'The Event Manager capability has been removed.',
      );
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsSaving(false);
    }
  }

  async function saveMobileNumber(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!selectedUser) return;

    setIsSaving(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      const response = await updateAdminPortalUserMobileNumber(
        token,
        selectedUser.LoginId,
        mobileNumber.trim(),
      );
      setMobileNumber(response.user.MobileNumber ?? '');
      await refreshUsers();
      setMessage('The mobile number has been updated.');
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteUser(): Promise<void> {
    if (!selectedUser || selectedUser.IsActive || selectedUser.IsEventManager
      || selectedUser.EventRoles.length > 0) return;
    const name = formatName(
      selectedUser.FirstName,
      selectedUser.LastName,
      selectedUser.Email,
    );
    if (!window.confirm(`Do you want to delete ${name}?`)) return;

    setIsSaving(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await deleteAdminPortalUser(token, selectedUser.LoginId);
      setSelectedLoginId(null);
      await refreshUsers();
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsSaving(false);
    }
  }

  async function releaseBooking(): Promise<void> {
    const booking = selectedUser?.ActiveBooking;
    if (!booking || !window.confirm(
      `Release ${booking.PhoneNumber} from ${selectedUser.Email}?`,
    )) return;

    setIsSaving(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await releasePortalPhoneBooking(token, booking.BookingId);
      await refreshUsers();
      setMessage('The telephone booking has been released.');
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="portal-section admin-users-page">
      <Link className="back-link" to="/admin">← Back to administration</Link>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Administration</p>
          <h1>Users</h1>
          <p className="hero-description">
            Find organization users, invite external participants and manage event access.
          </p>
        </div>
      </div>

      <section className="admin-user-search">
        <h2>Search for new YWAM users</h2>
        <label>
          Search by first name, last name or email
          <input
            type="search"
            value={searchQuery}
            placeholder="Enter at least two characters"
            onChange={(event) => {
              const value = event.target.value;
              setSearchQuery(value);
              if (value.trim().length < 2) {
                setSearchResults([]);
                setSearchMessage(null);
              }
            }}
          />
        </label>
        {isSearching ? <p>Searching...</p> : null}
        {searchMessage ? <p className="api-message" role="alert">{searchMessage}</p> : null}
        {searchResults.length > 0 ? (
          <div className="entra-search-results">
            {searchResults.map((result) => (
              <article key={result.EntraObjectId} className="entra-search-result">
                <div>
                  <strong>
                    {formatName(result.FirstName, result.LastName, result.Email)}
                    <span className="ywam-user-badge"
                      title="YWAM Norway organization account">
                      <img src="/uiotransperent.png" alt="" />
                      YWAM
                    </span>
                  </strong>
                  <span>{result.Email}</span>
                  <small>{result.Organization ?? 'No department'}</small>
                </div>
                <button
                  className={result.IsRegistered ? 'secondary-button' : 'primary-button'}
                  type="button"
                  disabled={isSaving || result.IsRegistered}
                  onClick={() => void importUser(result)}
                >
                  {result.IsRegistered ? 'Already in portal' : 'Add user'}
                </button>
              </article>
            ))}
          </div>
        ) : null}
      </section>

      {events.length > 0 ? (
        <GuestInvitationPanel
          events={events}
          assignableRoles={['EventManager', 'CallLead', 'Caller']}
        />
      ) : (
        <p>Create an event before inviting an external participant.</p>
      )}

      {message ? <p className="api-message" role="alert">{message}</p> : null}

      <section className="admin-user-list-section">
        <h2>Portal users</h2>
        {isLoading ? <p>Loading users...</p> : null}
        {!isLoading && users.length === 0 ? (
          <div className="event-empty-state"><p>No organization users have been added.</p></div>
        ) : null}
        {users.length > 0 ? (
          <CompactTable
            label="Portal users"
            headers={[
              {
                key: 'name',
                label: 'Name',
                onSort: () => changeSort('name'),
                sortDirection: sortDirection('name'),
              },
              {
                key: 'email',
                label: 'Email',
                onSort: () => changeSort('email'),
                sortDirection: sortDirection('email'),
              },
              {
                key: 'organization',
                label: 'Organization',
                onSort: () => changeSort('organization'),
                sortDirection: sortDirection('organization'),
              },
              {
                key: 'roles',
                label: 'Event roles',
                onSort: () => changeSort('roles'),
                sortDirection: sortDirection('roles'),
              },
              {
                key: 'status',
                label: 'Status',
                onSort: () => changeSort('status'),
                sortDirection: sortDirection('status'),
              },
              { key: 'actions', label: '' },
            ]}
          >
            {sortedUsers.map((user) => (
              <tr key={user.LoginId}>
                <td>
                  {formatName(user.FirstName, user.LastName, user.Email)}
                  {user.IdentityType === 'Guest' ? (
                    <span className="external-user-badge">External</span>
                  ) : null}
                  {user.IsEventManager ? (
                    <span className="role-badge">Event Manager</span>
                  ) : null}
                </td>
                <td>{user.Email}</td>
                <td>{user.Organization ?? '—'}</td>
                <td>{user.EventRoles.length}</td>
                <td>
                  <span className={getUserStatusClass(user)}>
                    {getUserStatus(user)}
                  </span>
                  {user.ActiveBooking ? (
                    <span className="user-status user-status-pending user-booking-status">
                      Booked
                    </span>
                  ) : null}
                </td>
                <td className="compact-table-actions">
                  <button className="table-action-button" type="button"
                    onClick={() => selectUser(user)}>
                    Manage
                  </button>
                </td>
              </tr>
            ))}
          </CompactTable>
        ) : null}
      </section>

      {selectedUser ? (
        <section className="admin-user-role-editor">
          <div className="admin-user-role-heading">
            <div>
              <p className="eyebrow">Event access</p>
              <h2>{formatName(
                selectedUser.FirstName,
                selectedUser.LastName,
                selectedUser.Email,
              )}</h2>
              <p>{selectedUser.Email}</p>
            </div>
            <button className="secondary-button" type="button"
              onClick={() => setSelectedLoginId(null)}>
              Close
            </button>
          </div>

          {selectedUser.ActiveBooking ? (
            <section className="admin-user-booking">
              <div>
                <p className="eyebrow">Current booking</p>
                <h3>{selectedUser.ActiveBooking.EventName ?? 'Standalone booking'}</h3>
                <p>
                  <strong>{selectedUser.ActiveBooking.PhoneNumber}</strong>
                  {' · '}Ends {formatDateTime(selectedUser.ActiveBooking.EndUtc)}
                </p>
              </div>
              <button className="secondary-button table-action-danger" type="button"
                disabled={isSaving} onClick={() => void releaseBooking()}>
                Release booking
              </button>
            </section>
          ) : (
            <p className="admin-user-no-booking">No active telephone booking.</p>
          )}

          <form className="admin-user-role-form admin-user-mobile-form"
            onSubmit={(event) => void saveMobileNumber(event)}>
            <label>
              Mobile number
              <input
                type="tel"
                required
                value={mobileNumber}
                placeholder="+4712345678"
                onChange={(event) => setMobileNumber(event.target.value)}
              />
            </label>
            <button className="primary-button" type="submit" disabled={isSaving}>
              Update
            </button>
          </form>
          <p>
            Used for login and Phonero verification codes sent by SMS.
            For organization users, a mobile number from Entra takes precedence.
          </p>

          <div className="admin-user-global-role">
            <div>
              <h3>Portal role</h3>
              <p>
                Event Managers can create new events. Access to an existing
                event is assigned separately below.
              </p>
            </div>
            <button className="secondary-button" type="button"
              disabled={isSaving || (!selectedUser.IsActive
                && !selectedUser.IsEventManager)}
              onClick={() => void setUserEventManager(
                !selectedUser.IsEventManager,
              )}>
              {selectedUser.IsEventManager
                ? 'Remove Event Manager'
                : 'Make Event Manager'}
            </button>
          </div>

          <div className="event-form-actions">
            <button className="secondary-button" type="button" disabled={isSaving}
              onClick={() => void setUserActiveState(!selectedUser.IsActive)}>
              {selectedUser.IsActive ? 'Deactivate user' : 'Activate user'}
            </button>
            <button className="secondary-button table-action-danger" type="button"
              disabled={isSaving || selectedUser.IsActive
                || selectedUser.IsEventManager
                || selectedUser.EventRoles.length > 0}
              onClick={() => void deleteUser()}>
              Delete user
            </button>
          </div>
          {selectedUser.IsActive ? (
            <p>Deactivate the user before deleting them.</p>
          ) : selectedUser.IsEventManager ? (
            <p>Remove the Event Manager capability before deleting the user.</p>
          ) : selectedUser.EventRoles.length > 0 ? (
            <p>Remove all event roles before deleting the user.</p>
          ) : (
            <p>This inactive user can be permanently deleted from the portal.</p>
          )}

          {events.length > 0 ? (
            <form className="admin-user-role-form" onSubmit={(event) => void saveRole(event)}>
              <label>
                Event
                <select required value={selectedEventId}
                  onChange={(event) => changeSelectedEvent(event.target.value)}>
                  <option value="">Select event</option>
                  {events.map((event) => (
                    <option key={event.EventId} value={event.EventId}>{event.Name}</option>
                  ))}
                </select>
              </label>
              <label>
                Role
                <select value={selectedRole}
                  onChange={(event) => setSelectedRole(event.target.value as PortalEventRole)}>
                  <option value="EventManager">Event Manager</option>
                  <option value="CallLead">Call Lead</option>
                  <option value="Caller">Caller</option>
                </select>
              </label>
              <button className="primary-button" type="submit"
                disabled={isSaving || !selectedEventId}>
                Save role
              </button>
            </form>
          ) : <p>Create an event before assigning event roles.</p>}

          {selectedUser.EventRoles.length > 0 ? (
            <CompactTable
              label="Current event roles"
              headers={[
                { key: 'event', label: 'Event' },
                { key: 'role', label: 'Role' },
                { key: 'actions', label: '' },
              ]}
            >
              {selectedUser.EventRoles.map((eventRole) => (
                <tr key={eventRole.EventId}>
                  <td>{eventRole.EventName}</td>
                  <td>{formatRole(eventRole.Role)}</td>
                  <td className="compact-table-actions">
                    <button className="table-action-button table-action-danger"
                      type="button" disabled={isSaving}
                      onClick={() => void removeRole(
                        eventRole.EventId,
                        eventRole.EventName,
                      )}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </CompactTable>
          ) : <p>This user has no event roles.</p>}
        </section>
      ) : null}
    </section>
  );
}

function getUserSortValue(user: AdminPortalUser, key: UserSortKey): string {
  if (key === 'name') return formatName(user.FirstName, user.LastName, user.Email);
  if (key === 'email') return user.Email;
  if (key === 'organization') return user.Organization ?? '';
  if (key === 'roles') {
    return `${user.IsEventManager ? 'Event Manager ' : ''}${user.EventRoles.length}`;
  }
  return getUserStatus(user);
}

function getUserStatus(user: AdminPortalUser): string {
  if (user.IsBlocked) return 'Blocked';
  if (!user.IsActive && user.InvitationStatus === 'Failed') return 'Delivery failed';
  if (!user.IsActive && (user.InvitationStatus === 'Pending'
    || user.InvitationStatus === 'Sent')) return 'Invitation pending';
  return user.IsActive ? 'Active' : 'Inactive';
}

function getUserStatusClass(user: AdminPortalUser): string {
  const status = getUserStatus(user);
  if (status === 'Active') return 'user-status user-status-active';
  if (status === 'Invitation pending') return 'user-status user-status-pending';
  return 'user-status user-status-blocked';
}

function formatName(firstName: string, lastName: string, fallback: string): string {
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || fallback;
}

function formatRole(role: PortalEventRole): string {
  if (role === 'EventManager') return 'Event Manager';
  if (role === 'CallLead') return 'Call Lead';
  return 'Caller';
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function toMessage(error: unknown): string {
  if (error instanceof PortalApiError || error instanceof Error) return error.message;
  return 'The user request could not be completed.';
}
